import { 
  getChatMessages, 
  getClientMessages, 
  getDialog,
  sendMessageToClient, 
  sendTypingStatus, 
  setMessageStatus,
  getUnreadMessageCount 
} from '@/api/chatApi';

const state = {
  chats: [],
  currentChat: null,
  unreadCount: 0,
  loading: false,
  error: null,
  activeTab: 0,
  tabs: ["Мои чаты", "Новые чаты", "Все чаты", "Чаты где общается бот"],
  selectedDate: new Date().toISOString().split('T')[0], // Сегодняшняя дата
  newMessages: [], // Новые сообщения для уведомлений
  typingStatus: {} // Статус набора текста по клиентам
};

const mutations = {
  SET_CHATS(state, chats) {
    state.chats = chats;
  },
  SET_CURRENT_CHAT(state, chat) {
    state.currentChat = chat;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_ACTIVE_TAB(state, tabIndex) {
    state.activeTab = tabIndex;
  },
  SET_SELECTED_DATE(state, date) {
    state.selectedDate = date;
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count;
  },
  ADD_NEW_MESSAGE(state, message) {
    state.newMessages.push(message);
  },
  CLEAR_NEW_MESSAGES(state) {
    state.newMessages = [];
  },
  UPDATE_CHAT_MESSAGES(state, { clientId, messages }) {
    const chatIndex = state.chats.findIndex(chat => chat.clientId === clientId);
    if (chatIndex !== -1) {
      state.chats[chatIndex].messages = messages;
    }
    if (state.currentChat && state.currentChat.clientId === clientId) {
      state.currentChat.messages = messages;
    }
  },
  ADD_MESSAGE_TO_CHAT(state, { clientId, message }) {
    const chatIndex = state.chats.findIndex(chat => chat.clientId === clientId);
    if (chatIndex !== -1) {
      // Проверяем, нет ли уже такого сообщения
      const existingMessage = state.chats[chatIndex].messages.find(msg => 
        msg.id === message.id || 
        (msg.text === message.text && msg.whoSend === message.whoSend && 
         Math.abs(new Date(msg.dateTime) - new Date(message.dateTime)) < 5000) // 5 секунд
      );
      
      if (!existingMessage) {
        state.chats[chatIndex].messages.push(message);
      }
    }
    
    if (state.currentChat && state.currentChat.clientId === clientId) {
      // Проверяем, нет ли уже такого сообщения в текущем чате
      const existingMessage = state.currentChat.messages.find(msg => 
        msg.id === message.id || 
        (msg.text === message.text && msg.whoSend === message.whoSend && 
         Math.abs(new Date(msg.dateTime) - new Date(message.dateTime)) < 5000) // 5 секунд
      );
      
      if (!existingMessage) {
        state.currentChat.messages.push(message);
      }
    }
  },
  SET_TYPING_STATUS(state, { clientId, isTyping }) {
    state.typingStatus[clientId] = isTyping;
  },
  MARK_CHAT_AS_READ(state, clientId) {
    const chatIndex = state.chats.findIndex(chat => chat.clientId === clientId);
    if (chatIndex !== -1) {
      state.chats[chatIndex].unreadCount = 0;
    }
    if (state.currentChat && state.currentChat.clientId === clientId) {
      state.currentChat.unreadCount = 0;
    }
  }
};

const actions = {
  async fetchChats({ commit, state, rootState }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      let payload;
      const formatDate = (date) => {
        const pad = (num) => String(num).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
          date.getDate()
        )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
          date.getSeconds()
        )}`;
      };

      switch (state.activeTab) {
        case 0: // Мои чаты
          if (rootState.user && rootState.user.login_talkme) {
            payload = {
              operator: { login: rootState.user.login_talkme },
              dialogClosed: false,
            };
          } else {
            commit('SET_CHATS', []);
            commit('SET_LOADING', false);
            return;
          }
          break;
        case 1: // Новые чаты
          payload = { haveNoAnswer: true };
          break;
        case 2: // Все чаты
          const startOfDay = new Date(state.selectedDate + "T00:00:00");
          const endOfDay = new Date(state.selectedDate + "T23:59:59");
          payload = {
            dateRange: {
              start: formatDate(startOfDay),
              stop: formatDate(endOfDay),
            },
          };
          break;
        case 3: // Чаты бота
          const startOfDays = new Date(state.selectedDate + "T00:00:00");
          const endOfDays = new Date(state.selectedDate + "T23:59:59");
          payload = {
            dateRange: {
              start: formatDate(startOfDays),
              stop: formatDate(endOfDays),
            },
          };
          break;
      }

      const chats = await getChatMessages(payload);
      
      console.log('Полученные чаты:', chats);
      if (chats.length > 0) {
        console.log('Структура первого чата:', JSON.stringify(chats[0], null, 2));
      }
      
      // Для вкладки "Чаты бота" фильтруем чаты без операторов
      if (state.activeTab === 3) {
        const filteredChats = chats.filter(chat => !chat.operators || chat.operators.length === 0);
        commit('SET_CHATS', filteredChats);
      } else {
        commit('SET_CHATS', chats);
      }
    } catch (error) {
      commit('SET_ERROR', error.message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async sendMessage({ commit, state, rootState }, { clientId, messageText }) {
    try {
      const payload = {
        clientId,
        operatorLogin: rootState.user.login_talkme,
        messageText
      };

      const result = await sendMessageToClient(payload);

      // Добавляем сообщение локально для мгновенного отображения
      const newMessage = {
        id: result.id || Date.now(), // Используем ID из ответа или временную метку
        text: messageText,
        whoSend: 'operator',
        operatorName: rootState.user.label,
        dateTime: new Date().toISOString(),
        isVisibleForClient: true
      };

      commit('ADD_MESSAGE_TO_CHAT', { clientId, message: newMessage });
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async getClientMessages({ commit }, { clientId, dialogId, limit = 30, page = 0 }) {
    try {
      let result;
      
      // Если есть dialogId, используем getDialog
      if (dialogId) {
        const dialogPayload = { dialogId };
        result = await getDialog(dialogPayload);
        
        // Обновляем сообщения для чата
        if (result && result.messages) {
          commit('UPDATE_CHAT_MESSAGES', { 
            clientId, 
            messages: result.messages 
          });
        }
      } else {
        // Используем старый метод с clientId
        const payload = {
          clientId,
          limit,
          page,
          orderDirection: "desc"
        };

        result = await getClientMessages(payload);

        commit('UPDATE_CHAT_MESSAGES', { 
          clientId, 
          messages: result.items.reverse() 
        });
      }
      
      return result;
    } catch (error) {
      // Не устанавливаем глобальную ошибку, так как это не должно влиять на список чатов
      console.error('Ошибка загрузки сообщений для чата:', clientId, error);
      // Возвращаем пустой массив сообщений вместо выброса ошибки
      commit('UPDATE_CHAT_MESSAGES', { 
        clientId, 
        messages: [] 
      });
      return { items: [] };
    }
  },

  async markMessageAsRead({ commit }, { messageId, operatorLogin }) {
    try {
      const payload = {
        messageId,
        status: "readed",
        operatorLogin
      };

      const result = await setMessageStatus(payload);
      commit('MARK_CHAT_AS_READ', messageId);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async sendTypingStatus({ commit, rootState }, { clientId, ttl = 30 }) {
    try {
      const payload = {
        clientId,
        operatorLogin: rootState.user.login_talkme,
        ttl
      };

      const result = await sendTypingStatus(payload);
      
      commit('SET_TYPING_STATUS', { clientId, isTyping: true });
      
      // Автоматически сбрасываем статус через ttl секунд
      setTimeout(() => {
        commit('SET_TYPING_STATUS', { clientId, isTyping: false });
      }, ttl * 1000);
      
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  handleNewMessage({ commit, state, rootState }, messageData) {
    // Обрабатываем новое сообщение из WebSocket
    const { clientId, message, operatorLogin } = messageData;
    
    // Проверяем, предназначено ли сообщение для текущего оператора
    if (operatorLogin === rootState.user.login_talkme) {
      commit('ADD_MESSAGE_TO_CHAT', { clientId, message });
      
      // Если чат не открыт, добавляем в уведомления
      if (!state.currentChat || state.currentChat.clientId !== clientId) {
        commit('ADD_NEW_MESSAGE', messageData);
      }
    }
  },

  handleClientNotification({ commit, rootState }, notificationData) {
    // Обрабатываем уведомление о новом клиенте
    const { clientId, name, lastName, phone, operatorLogin } = notificationData;
    
    // Проверяем, предназначено ли уведомление для текущего оператора
    if (operatorLogin === rootState.user.login_talkme) {
      commit('ADD_NEW_MESSAGE', {
        type: 'clientNotification',
        clientId,
        name,
        lastName,
        phone
      });
    }
  }
};

const getters = {
  currentChats: (state) => {
    return state.chats;
  },
  
  unreadChatsCount: (state) => {
    return state.chats.filter(chat => chat.unreadCount > 0).length;
  },
  
  hasNewMessages: (state) => {
    return state.newMessages.length > 0;
  },
  
  getChatById: (state) => (clientId) => {
    return state.chats.find(chat => chat.clientId === clientId);
  },
  
  isTyping: (state) => (clientId) => {
    return state.typingStatus[clientId] || false;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
